import type { ReactNode } from 'react';
import { ActionBar, BackButton } from '@/components';
import { ActionType } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { Tabs, type TabItem } from '@/components/Tabs';
import {
    DirectoryItemHeader,
    DirectoryItemInfoPanel,
    DirectoryItemChart,
    type DirectoryItemInfoField,
    type PeriodOption,
} from './components';
import type { DirectoryItemAction, DirectoryItemSourceStats } from './types';
import type { ChartDataPoint } from '@/components/Chart';
import { cn } from '@/lib/utils';
import { SidePanel } from '@/components/SidePanel';
import { useEventFeedStore } from '@/stores/event-feed.store';

interface TabsContent {
    kind: 'tabs';
    tabs: TabItem[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
    tabContent: ReactNode;
}

interface ChartContent {
    kind: 'chart';
    chartData: ChartDataPoint[];
    totalEvents: number;
    sourceStats: DirectoryItemSourceStats[];
    isLoading: boolean;
    activePeriod: PeriodOption;
    onPeriodChange: (period: PeriodOption) => void;
    onPointClick?: (dataPoint: ChartDataPoint, index: number) => void;
    onSourceClick?: (source: DirectoryItemSourceStats) => void;
}

type DirectoryItemContent = TabsContent | ChartContent;

interface DirectoryItemPageProps {
    headerIcon: ReactNode;
    headerImageUrl?: string;
    title: string;
    badge?: ReactNode;
    tags?: string[];
    headerRightContent?: ReactNode;
    actions: DirectoryItemAction[];
    infoFields?: DirectoryItemInfoField[];
    content?: DirectoryItemContent;
    eventFeed: ReactNode;
    className?: string;
}

export function DirectoryItemPage({
    headerIcon,
    headerImageUrl,
    title,
    badge,
    tags,
    headerRightContent,
    actions,
    infoFields,
    content,
    eventFeed,
    className,
}: DirectoryItemPageProps) {
    const playerIsFullscreen = useEventFeedStore(
        (state) => state.playerIsFullscreen,
    );
    const visibleActions = actions.filter((action) => !action.hidden);
    const actionConfigs: ActionConfig[] = visibleActions.map((action) => ({
        type: ActionType.ITEM,
        icon: action.icon,
        label: action.label,
        onClick: action.onClick,
        disabled: action.disabled,
    }));

    return (
        <div className={cn('h-full relative', className)}>
            <BackButton variant="floating" />

            <SidePanel side="right" align="stretch">
                {eventFeed}
            </SidePanel>

            <div className="relative h-full min-h-0 flex flex-col items-center">
                <div
                    className={cn(
                        'absolute top-6 left-1/2 -translate-x-1/2 z-40',
                        playerIsFullscreen && 'hidden',
                    )}
                >
                    {visibleActions.length > 0 && (
                        <ActionBar actions={actionConfigs} />
                    )}
                </div>

                <div className="w-full flex-1 min-h-0">
                    <div className="h-full flex flex-col relative overflow-hidden pt-14">
                        <div className="flex-1 flex overflow-y-auto scrollbar-hide px-4">
                            <div className="flex-1 flex flex-col gap-6 py-8 mb-[50px] min-w-0 max-w-content mx-auto">
                                <DirectoryItemHeader
                                    icon={headerIcon}
                                    imageUrl={headerImageUrl}
                                    title={title}
                                    badge={badge}
                                    tags={tags}
                                    rightContent={headerRightContent}
                                />

                                {infoFields && infoFields.length > 0 && (
                                    <div className="flex flex-col border-b border-grey-500 @wide:flex-row">
                                        <DirectoryItemInfoPanel
                                            fields={infoFields}
                                        />
                                    </div>
                                )}

                                {content?.kind === 'tabs' && (
                                    <>
                                        <Tabs
                                            tabs={content.tabs}
                                            activeTab={content.activeTab}
                                            onTabChange={content.onTabChange}
                                        />
                                        <div className="flex-1 min-h-0">
                                            {content.tabContent}
                                        </div>
                                    </>
                                )}

                                {content?.kind === 'chart' && (
                                    <div className="flex-1 min-h-0">
                                        <DirectoryItemChart
                                            chartData={content.chartData}
                                            totalEvents={content.totalEvents}
                                            sourceStats={content.sourceStats}
                                            isLoading={content.isLoading}
                                            activePeriod={content.activePeriod}
                                            onPeriodChange={
                                                content.onPeriodChange
                                            }
                                            onPointClick={content.onPointClick}
                                            onSourceClick={
                                                content.onSourceClick
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
